import { useCallback, useRef, useState } from 'react';

import { IconButton as ButtonIcon, styled } from '@material-ui/core';

import { Node, mergeAttributes } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import {
  NodeViewContent,
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';

import { CommentZone } from '@spotify/plugin-confidence-comments-react';

import { useBlockZoneId } from '../../hooks';
import { blockIdAttribute, generateBlockId } from '../../utils';
import { TagIconPicker } from '../Tag/TagIconPicker';
import { TAG_ICON_COMPONENTS, type TagIconName } from '../Tag/constants';
import { tiptapBlockMarginBottom, tiptapBlockMarginTop } from '../editorStyles';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    highlightBlock: {
      setHighlight: () => ReturnType;
      toggleHighlight: () => ReturnType;
    };
  }
}

const DEFAULT_ICON: TagIconName = 'RiCheckboxCircleLine';

const StyledHighlight = styled('div')({
  width: '100%',
  position: 'relative',
});

const ContentWrapper = styled(NodeViewContent)({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: tiptapBlockMarginBottom,
  marginTop: tiptapBlockMarginTop,

  '& .caption': {
    paddingBottom: 0,
  },
  '& p.editor-paragraph': {
    paddingLeft: 'var(--encore-spacing-looser-2)',
    paddingTop: 0,
  },
});

const iconButtonSize = 32; // ButtonIcon size="small"
const IconWrapper = styled('span')({
  position: 'absolute',
  left: 0,
  // Center the icon button with the first line of the paragraph below the caption.
  top: `calc(var(--encore-text-size-smaller-2) * 1.4 + var(--encore-spacing-tighter-4) * 2 + (var(--encore-text-size-base) * 1.5 - ${iconButtonSize}px) / 2)`,
  lineHeight: 0,
});

const HighlightComponent = ({ node, updateAttributes }: NodeViewProps) => {
  const [showIconPicker, setShowIconPicker] = useState(false);
  const iconButtonRef = useRef<HTMLSpanElement>(null);

  const { blockId } = node.attrs;
  const zoneId = useBlockZoneId('highlight', blockId, updateAttributes);
  const iconName = (node.attrs.icon as TagIconName) || DEFAULT_ICON;
  const IconComponent = TAG_ICON_COMPONENTS[iconName];

  const handleIconClick = useCallback(() => {
    setShowIconPicker(true);
  }, []);

  const handleIconSelect = useCallback(
    (icon: TagIconName) => {
      updateAttributes({ icon });
      setShowIconPicker(false);
    },
    [updateAttributes],
  );

  const handleClose = useCallback(() => {
    setShowIconPicker(false);
  }, []);

  const content = (
    <>
      <StyledHighlight>
        <ContentWrapper />
        <IconWrapper ref={iconButtonRef} contentEditable={false}>
          <ButtonIcon
            size="small"
            aria-label="Change icon"
            onClick={handleIconClick}
          >
            <IconComponent size={24} />
          </ButtonIcon>
        </IconWrapper>
      </StyledHighlight>
      {showIconPicker && iconButtonRef.current && (
        <TagIconPicker
          anchorEl={iconButtonRef.current}
          onSelect={handleIconSelect}
          onClose={handleClose}
          currentIcon={iconName}
        />
      )}
    </>
  );

  return (
    <NodeViewWrapper>
      {zoneId ? (
        <CommentZone id={zoneId} component="div">
          {content}
        </CommentZone>
      ) : (
        content
      )}
    </NodeViewWrapper>
  );
};

export const Highlight = Node.create({
  name: 'highlightBlock',
  group: 'block',
  content: 'caption paragraph',
  defining: true,

  addAttributes() {
    return {
      icon: {
        default: DEFAULT_ICON,
        parseHTML: element => element.getAttribute('data-icon'),
        renderHTML: attributes => ({
          'data-icon': attributes.icon,
        }),
      },
      blockId: blockIdAttribute,
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="highlight"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'highlight' }),
      0,
    ];
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(HighlightComponent);
  },

  addKeyboardShortcuts() {
    return {
      // Handle Enter inside highlight block:
      // - Caption: move cursor to the paragraph
      // - Paragraph: exit the highlight block
      Enter: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from, empty } = selection;

        if (!empty) return false;

        // Find the enclosing highlightBlock
        for (let depth = $from.depth - 1; depth >= 0; depth--) {
          const node = $from.node(depth);
          if (node.type.name !== 'highlightBlock') continue;

          // Caption: move cursor to the start of the paragraph
          if ($from.parent.type.name === 'caption') {
            const indexInHighlight = $from.index(depth);
            if (indexInHighlight === 0 && node.childCount > 1) {
              // Move to the start of the next sibling (the paragraph)
              const paragraphPos = $from.after($from.depth) + 1;
              editor
                .chain()
                .command(({ tr }) => {
                  tr.setSelection(
                    TextSelection.near(tr.doc.resolve(paragraphPos)),
                  );
                  return true;
                })
                .run();
              return true;
            }
            return true;
          }

          // Paragraph: exit the highlight block by inserting a paragraph after it
          if ($from.parent.type.name === 'paragraph') {
            const highlightPos = $from.before(depth);
            const endOfHighlight = highlightPos + node.nodeSize;

            editor
              .chain()
              .command(({ tr }) => {
                tr.insert(
                  endOfHighlight,
                  state.schema.nodes.paragraph.create(),
                );
                tr.setSelection(
                  TextSelection.near(tr.doc.resolve(endOfHighlight + 1)),
                );
                return true;
              })
              .run();

            return true;
          }

          break;
        }

        return false;
      },

      // Handle backspace at the start of the first child to prevent caption format leaking
      Backspace: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from, empty } = selection;

        if (!empty || $from.parentOffset !== 0) return false;

        // Find the enclosing highlightBlock
        for (let depth = $from.depth - 1; depth >= 0; depth--) {
          const node = $from.node(depth);
          if (node.type.name !== 'highlightBlock') continue;

          const indexInHighlight = $from.index(depth);

          // At the start of the caption (first child) → delete the entire highlight,
          // replace with a paragraph containing the caption text
          if (indexInHighlight === 0 && $from.parent.type.name === 'caption') {
            const highlightPos = $from.before(depth);

            editor
              .chain()
              .command(({ tr }) => {
                // Collect text content from all children to preserve in a paragraph
                const textContent = node.textContent;
                const newParagraph = textContent
                  ? state.schema.nodes.paragraph.create(
                      null,
                      state.schema.text(textContent),
                    )
                  : state.schema.nodes.paragraph.create();

                tr.replaceWith(
                  highlightPos,
                  highlightPos + node.nodeSize,
                  newParagraph,
                );
                tr.setSelection(
                  TextSelection.near(tr.doc.resolve(highlightPos + 1)),
                );
                return true;
              })
              .run();

            return true;
          }

          // At the start of a paragraph right after caption → prevent merging with caption
          if (
            indexInHighlight === 1 &&
            $from.parent.type.name === 'paragraph'
          ) {
            return true;
          }

          break;
        }

        return false;
      },
    };
  },

  addCommands() {
    return {
      setHighlight:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { icon: DEFAULT_ICON, blockId: generateBlockId() },
            content: [
              {
                type: 'caption',
                content: [{ type: 'text', text: 'Label' }],
              },
              {
                type: 'paragraph',
                content: [{ type: 'text', text: 'Text...' }],
              },
            ],
          });
        },
      toggleHighlight:
        () =>
        ({ commands }) => {
          return commands.toggleWrap(this.name);
        },
    };
  },
});
